/**
 * Vault — listado (F3a): tiles de salud, tabs, búsqueda, tabla y alta.
 * Visibilidad filtrada por las áreas de la persona (la resuelve el backend).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, KeyRound, Lock, Plus, RotateCw, Search, ShieldAlert, Unlock, Upload, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { usePagination } from '@/hooks/usePagination';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EmptyState, LoaderBlock, StatCard, fmtNum } from '@/components/internal/shared';
import { AreaBadge, MfaBadge, PermissionBadge, fmtRelative } from '@/components/vault/vault-ui';
import { SecretFormDialog } from '@/components/vault/SecretFormDialog';
import { ImportDialog } from '@/components/vault/ImportDialog';
import { useUnlockPrompt } from '@/components/vault/UnlockDialog';
import { errMsg, useVaultAccess, useVaultSession } from '@/hooks/useVault';
import { VAULT_AREA_LABEL } from '../../../../shared/types/vault';

type Tab = 'all' | 'mine' | 'shared';
const ALL = '__all__';

export function VaultPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const access = useVaultAccess();
  const list = trpc.vault.listSecrets.useQuery(undefined, { staleTime: 60_000 });
  const { unlocked, lock } = useVaultSession();
  const { ensureUnlocked, dialog } = useUnlockPrompt();

  const [tab, setTab] = useState<Tab>('all');
  const [q, setQ] = useState('');
  const [area, setArea] = useState(ALL);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const areas = useMemo(() => [...new Set(items.map((i) => i.area).filter((a): a is string => !!a))].sort(), [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((i) => {
      // "Mías" = soy owner (permiso efectivo), no el ownerEmail de cabecera
      if (tab === 'mine' && i.myPermission !== 'owner') return false;
      if (tab === 'shared' && i.myPermission === 'owner') return false;
      if (area !== ALL && i.area !== area) return false;
      if (!needle) return true;
      return [i.name, i.username, i.clientId ?? '', i.url ?? '', ...(i.tags ?? [])].some((s) => s.toLowerCase().includes(needle));
    });
  }, [items, tab, area, q]);

  const stats = list.data?.stats;
  const { pageItems, footer: paginationFooter } = usePagination(filtered, 25);

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-2 sm:p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/30">
            <KeyRound className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl">{t('vault.title', 'Vault de Winclap')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('vault.subtitle', 'Credenciales compartidas, cifradas con AWS KMS y auditadas. Ves solo lo de tus áreas del community map y lo que te compartieron.')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unlocked ? (
            <Button variant="outline" size="sm" onClick={lock} title={t('vault.lock', 'Bloquear ahora')} data-tour="lock-toggle">
              <Unlock className="mr-2 h-4 w-4 text-brand-green" />
              {t('vault.unlocked', 'Desbloqueado')}
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => ensureUnlocked()} data-tour="lock-toggle">
              <Lock className="mr-2 h-4 w-4" />
              {t('vault.locked', 'Bloqueado')}
            </Button>
          )}
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            {t('vault.import.button', 'Importar CSV')}
          </Button>
          <Button onClick={() => setFormOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('vault.new', 'Nueva credencial')}
          </Button>
          <ImportDialog open={importOpen} onOpenChange={setImportOpen} ensureUnlocked={ensureUnlocked} />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-tour="stats">
        <StatCard icon={KeyRound} label={t('vault.stats.total', 'Credenciales')} value={fmtNum(stats?.total ?? 0)} tone="blue" />
        <StatCard icon={Users} label={t('vault.stats.shared_accounts', 'Cuentas compartidas')} value={stats?.sharedAccounts == null ? '—' : fmtNum(stats.sharedAccounts)} sub={t('vault.stats.shared_accounts_sub', 'según el censo')} tone="purple" />
        <StatCard icon={ShieldAlert} label={t('vault.stats.without_totp', 'Sin MFA')} value={fmtNum(stats?.withoutTotp ?? 0)} tone={(stats?.withoutTotp ?? 0) > 0 ? 'amber' : 'green'} />
        <StatCard icon={RotateCw} label={t('vault.stats.rotation', 'Con rotación sugerida')} value={fmtNum(stats?.needsRotation ?? 0)} tone={(stats?.needsRotation ?? 0) > 0 ? 'amber' : 'green'} />
      </div>

      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              <TabsTrigger value="all" data-tour="tab-all">{t('vault.tabs.all', 'Todas')}</TabsTrigger>
              <TabsTrigger value="mine" data-tour="tab-mine">{t('vault.tabs.mine', 'Mías')}</TabsTrigger>
              <TabsTrigger value="shared" data-tour="tab-shared">{t('vault.tabs.shared', 'Compartidas conmigo')}</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-8" placeholder={t('vault.search', 'Buscar por nombre, cuenta, cliente o URL…')} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('vault.filter.all_areas', 'Área: todas')}</SelectItem>
              {areas.map((a) => (
                <SelectItem key={a} value={a}>{VAULT_AREA_LABEL[a] ?? a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {list.isLoading ? (
          <LoaderBlock />
        ) : list.isError ? (
          // un error nunca se disfraza de "todavía no hay credenciales" (política de roles y visibilidad)
          <EmptyState title={t('vault.empty.load_error', 'No se pudo cargar el Vault')} description={errMsg(list.error)} />
        ) : filtered.length === 0 ? (
          <EmptyState
            title={items.length === 0 ? t('vault.empty.title', 'Todavía no hay credenciales') : t('vault.empty.filtered', 'Nada coincide con el filtro')}
            description={items.length === 0 ? t('vault.empty.body', 'Creá la primera con "Nueva credencial" o pedile al owner que te comparta.') : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('vault.cols.secret', 'Credencial')}</TableHead>
                  <TableHead>{t('vault.cols.area', 'Área')}</TableHead>
                  <TableHead>{t('vault.cols.client', 'Cliente')}</TableHead>
                  <TableHead>{t('vault.cols.password', 'Contraseña')}</TableHead>
                  <TableHead>MFA</TableHead>
                  <TableHead>{t('vault.cols.rotation', 'Última rotación')}</TableHead>
                  <TableHead data-tour="col-access">{t('vault.cols.access', 'Mi acceso')}</TableHead>
                  <TableHead className="text-right">{t('vault.cols.actions', 'Acciones')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((s) => (
                  <TableRow key={s.secretId} className="cursor-pointer" onClick={() => navigate(`/vault/secrets/${s.secretId}`)} data-tour={`secret-${s.secretId}`}>
                    <TableCell>
                      <div className="font-medium">{s.name}</div>
                      <div className="text-xs text-muted-foreground">{s.username}</div>
                    </TableCell>
                    <TableCell><AreaBadge area={s.area} /></TableCell>
                    <TableCell>{s.clientId ?? '—'}</TableCell>
                    <TableCell className="font-mono text-muted-foreground">••••••••</TableCell>
                    <TableCell><MfaBadge hasTotp={s.hasTotp} /></TableCell>
                    <TableCell className={s.needsRotation ? 'text-amber-700' : ''}>{fmtRelative(s.lastRotatedAt, i18n.language)}</TableCell>
                    <TableCell><PermissionBadge permission={s.myPermission} /></TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="icon" title={t('vault.open', 'Ver')} aria-label={t('vault.open', 'Ver')} onClick={(e) => { e.stopPropagation(); navigate(`/vault/secrets/${s.secretId}`); }}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {paginationFooter ?? (
              <p className="mt-3 text-xs text-muted-foreground">
                {t('vault.showing', 'Mostrando {{n}} de {{total}} credenciales', { n: filtered.length, total: items.length })}
              </p>
            )}
          </div>
        )}
      </Card>

      <SecretFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        principals={access.data?.principals ?? []}
        isAdmin={!!access.data?.isAdmin}
        ensureUnlocked={ensureUnlocked}
        onCreated={(id) => navigate(`/vault/secrets/${id}`)}
      />
      {dialog}
    </div>
  );
}
